import { motion } from "framer-motion";
import { ArrowDown, Ruler, ListTodo, BadgeCheck, MapPin, FileText } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const CHAOS = [
  { me: false, t: "morning, on my way" },
  { me: true, t: "great — gate opening is 4200mm ok?" },
  { me: false, t: "yep measured twice" },
  { me: true, t: "can you order motors by Friday?" },
  { me: false, t: "send me the address again?" },
  { me: true, t: "14 Oakfield Drive, did you get the drawings?" },
  { me: false, t: "which message were they in??" },
  { me: true, t: "scroll up… somewhere above the dog photo" },
];

const SAVED = [
  { icon: Ruler, label: "Measurement", value: "4200mm · gate opening" },
  { icon: ListTodo, label: "Task", value: "Order gate motors · Friday" },
  { icon: MapPin, label: "Address", value: "14 Oakfield Drive" },
  { icon: FileText, label: "Document", value: "gate-drawings.pdf" },
  { icon: BadgeCheck, label: "Approval", value: "Anthracite frames · Sarah" },
];

export const Problem = () => (
  <section className="bg-diem-ink py-28 sm:py-36 relative overflow-hidden" data-testid="problem-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="01" label="The problem" dark />
      <div className="mt-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal delay={0.1}>
            <SectionHeading dark>Your messages already run your life.</SectionHeading>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg leading-relaxed text-white/60 max-w-lg">
              Plans. Photos. Addresses. Decisions. Measurements. Deadlines. Documents. They're already
              being sent in messages.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-white font-medium max-w-lg">
              The problem is finding them again.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <button
              data-testid="problem-cta"
              onClick={() => document.querySelector("#features")?.scrollIntoView({ behavior: "smooth" })}
              className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-white text-diem-ink font-semibold px-7 py-3.5 hover:bg-diem-sky hover:-translate-y-0.5 active:scale-[0.98] transition-[background-color,transform] duration-300"
            >
              Meet messaging that remembers
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </Reveal>
        </div>

        <div className="relative">
          <Reveal delay={0.2}>
            <div className="relative rounded-3xl bg-white/[0.04] border border-white/10 p-5 backdrop-blur">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">The old way</p>
                <span className="text-[11px] font-bold text-diem-warn bg-diem-warn/10 rounded-full px-2.5 py-1">
                  247 messages
                </span>
              </div>
              <div className="space-y-2.5 fade-top-mask h-[300px] overflow-hidden">
                {CHAOS.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: m.me ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.09, duration: 0.5 }}
                    className={`max-w-[85%] rounded-xl px-3.5 py-2 text-[12.5px] leading-snug ${
                      m.me
                        ? "ml-auto bg-white/10 text-white/60"
                        : "bg-white/[0.06] text-white/45"
                    }`}
                  >
                    {m.t}
                  </motion.div>
                ))}
              </div>
              <p className="mt-4 text-center text-[12px] text-white/35 italic">
                Somewhere in here is a measurement you need right now.
              </p>
            </div>
          </Reveal>

          <motion.div
            initial={{ opacity: 0, y: 40, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative sm:absolute sm:-bottom-16 sm:-left-10 mt-6 sm:mt-0 w-full sm:w-[290px] rounded-3xl bg-white p-5 shadow-[0_30px_70px_-10px_rgba(0,0,0,0.5)]"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-diem-blue mb-3">The Deyem way</p>
            <div className="space-y-2">
              {SAVED.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, x: 14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + i * 0.1, duration: 0.45 }}
                  className="flex items-center gap-3 rounded-xl bg-diem-paper border border-diem-line px-3 py-2"
                >
                  <s.icon className="w-4 h-4 text-diem-blue shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-diem-muted">{s.label}</p>
                    <p className="text-[12px] font-semibold text-diem-ink truncate">{s.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
